import { Button, Chip, EmptyState } from "@ondit/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Copy, PenLine } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { RunWithAgentButton } from "~/common/agent-run/run-with-agent";
import { useTopUp } from "~/common/billing/topup";
import { useAccess, whyDisabled } from "~/core/access/index";
import { ApiError, api, type CitedPage, type Opportunity } from "~/core/api/index";
import * as fmt from "~/core/format";
import { useI18n, useT } from "~/core/i18n";
import { keys, read } from "~/core/query";
import { Section, Stat } from "~/core/ui/page";
import { Async } from "~/core/ui/states";
import { Drill } from "~/modules/analytics/components/detail";
import { type ReadingProps, StageShell } from "~/modules/analytics/components/stage";
import { useWriteAbout } from "~/modules/analytics/lib/write";

/** The four kinds of move, in the order the plan is worth reading. */
const CATEGORIES = ["listing", "owned", "outreach", "community"] as const;

/**
 * What to do about the measurements.
 *
 * Everything else in this panel is a number. This is the one screen that says
 * what to do next, and it says it about places ondit cannot publish as readily
 * as about the ones it can — a plan that only named ondit's own product would be
 * an advert, and the customer would find that out in about a month.
 *
 * The report is stored and served as it stands; only the button spends. That is
 * the whole reason the refresh is explicit rather than automatic on load.
 */
export function OpportunitiesPage({ entityId, filters, setFilters }: ReadingProps) {
	const t = useT();
	const topUp = useTopUp();
	const { locale } = useI18n();
	const { editor, why } = useAccess();
	const queryClient = useQueryClient();
	const writing = useWriteAbout(entityId, filters.locale);
	const state = useQuery({
		queryKey: [...keys.entity(entityId), "opportunities", filters.locale],
		queryFn: () => api.opportunities(entityId, { locale: filters.locale }),
		...read,
	});
	const waitFor = useJobWatch();
	// The write is the request and the wait together: the report it asks for
	// is only worth reading again once the job has ended.
	const report = useMutation({
		mutationFn: async () => waitFor((await api.generateOpportunities(entityId, { locale: filters.locale })).job_id),
		onSuccess: () => void queryClient.invalidateQueries({ queryKey: keys.entity(entityId) }),
	});
	const busy = report.isPending;

	const generate = useCallback(async () => {
		try {
			const ending = await report.mutateAsync();
			if (ending === "failed") toast.error(t("opps.failed"), { duration: Infinity });
			else if (ending === "running") toast(t("opps.running"));
			else if (ending === "skipped") toast.warning(t("opps.skipped"));
			else toast.success(t("opps.done"));
		} catch (e) {
			// The refusal is the useful part — "12 answers in the last 30 days"
			// tells the customer to go and measure, which is the actual next
			// step. It stays until dismissed.
			toast.error(
				e instanceof ApiError ? (e.code === "bad_request" && e.detail ? e.detail : e.localized(locale)) : String(e),
				{ duration: Infinity },
			);
		}
	}, [locale, report.mutateAsync, t]);

	return (
		<StageShell
			entityId={entityId}
			reading="opportunities"
			lede={t("opps.lede")}
			filters={filters}
			setFilters={setFilters}
			actions={
				// The one filled control on this screen, in the head with the scope
				// that decides what it will read. It was a button in the body under
				// three figures, which put the only thing you can *do* here below
				// the fold on a report long enough to be worth writing.
				<>
					{state.data?.id ? (
						<RunWithAgentButton brandId={entityId} event="opportunities_ready" objectId={state.data.id} />
					) : null}
					<Chip
						variant={state.data?.stale === false ? undefined : "primary"}
						type="button"
						disabled={
							busy || !editor || state.data === undefined || state.data.blocked !== null || state.data.balance <= 0
						}
						title={whyDisabled(editor, why.editor)}
						onClick={generate}
					>
						{busy
							? t("opps.working")
							: state.data === undefined || !state.data.report
								? t("opps.generate")
								: t("opps.refresh")}
					</Chip>
				</>
			}
		>
			<Async state={state}>
				{(data) => (
					<>
						{/* Why the button in the head will not press, directly under it.
						    Only ever one line, and only when there is one to say. */}
						{data.blocked || data.balance <= 0 ? (
							<div className="row-actions opps-note">
								{data.blocked ? (
									<span className="faint">
										{t("opps.blocked", {
											answers: String(data.blocked.answers),
											needed: String(data.blocked.needed),
											days: String(data.window_days),
										})}
									</span>
								) : (
									<>
										<span className="faint">{t("gaps.noCredits")}</span>
										<Button variant="link" type="button" onClick={() => topUp("credits")}>
											{t("topup.open")}
										</Button>
									</>
								)}
							</div>
						) : null}

						<div className="stats">
							<Stat
								label={t("opps.generatedLabel")}
								hint={t("hint.oppsFresh")}
								mono={false}
								value={data.generated_at ? fmt.date(locale, data.generated_at) : "—"}
								foot={data.model ?? undefined}
							/>
							<Stat label={t("opps.windowLabel")} value={t("opps.windowDays", { n: data.window_days })} mono={false} />
						</div>

						{data.report === null ? (
							<EmptyState size="compact" title={t("opps.emptyTitle")} description={t("opps.empty")} />
						) : (
							<>
								{/* The picture, in the width it was given. It was set in a
								    64-character measure — the measure for prose — and the
								    report is not prose: it is three or four findings, each
								    of them a line long, and a column of them down the left
								    of a workbench screen wrapped every one of them while
								    half the screen stayed empty. One finding per panel,
								    laid out across the width there is. */}
								{data.report.summary.length > 0 ? (
									<Section title={t("opps.summary")}>
										<ol className="opps-points">
											{data.report.summary.map((line) => (
												<li key={line}>{line}</li>
											))}
										</ol>
									</Section>
								) : null}

								{CATEGORIES.map((category) => {
									const rows = data.report?.opportunities.filter((o) => o.category === category) ?? [];
									if (rows.length === 0) return null;
									return (
										<Section
											key={category}
											title={t(`opps.category.${category}`)}
											lede={t(`opps.categoryHint.${category}`)}
											action={<span className="faint">{t("opps.nMoves", { n: rows.length })}</span>}
										>
											<div className="opps">
												{rows.map((item) => (
													<Card
														key={item.title}
														item={item}
														generatedAt={data.generated_at}
														// Only a page of the brand's own is something to write
														// here; a listing, a pitch or a thread is somebody
														// else's page.
														write={
															category === "owned"
																? {
																		start: () => writing.write(item.questions[0] ?? item.title),
																		disabled: !editor || writing.busy !== null,
																	}
																: undefined
														}
													/>
												))}
											</div>
										</Section>
									);
								})}

								{data.report.risks.length > 0 ? (
									<Section title={t("opps.risks")} lede={t("opps.risksHint")}>
										<ul className="opps-points is-risk">
											{data.report.risks.map((line) => (
												<li key={line}>{line}</li>
											))}
										</ul>
									</Section>
								) : null}
							</>
						)}
					</>
				)}
			</Async>
		</StageShell>
	);
}

/**
 * One opportunity, and the brief that leaves with it.
 *
 * The copy button is the point of the card. What a person does after reading
 * this is hand it to whoever writes the thing, and retyping it out of four
 * collapsed panels is where a plan stops being followed.
 */
function Card({
	item,
	generatedAt,
	write,
}: {
	item: Opportunity;
	generatedAt: string | null;
	write?: { start: () => void; disabled: boolean };
}) {
	const t = useT();
	const { locale } = useI18n();
	const [copied, setCopied] = useState(false);

	async function copy() {
		try {
			await navigator.clipboard.writeText(toMarkdown(item, t, generatedAt ? fmt.date(locale, generatedAt) : null));
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		} catch {
			// Clipboard access can be refused. Everything in the card is on the
			// page and selectable.
			toast.error(t("opps.copyFailed"));
		}
	}

	return (
		<article className="card">
			<div className="card-head">
				<h3>{item.title}</h3>
				<div className="row-actions">
					{write ? (
						<Button variant="default" type="button" disabled={write.disabled} onClick={write.start}>
							<PenLine aria-hidden />
							{t("opps.write")}
						</Button>
					) : null}
					<Button variant="default" type="button" onClick={copy} aria-label={t("opps.copy")}>
						{copied ? <Check aria-hidden /> : <Copy aria-hidden />}
						{copied ? t("opps.copied") : t("opps.copy")}
					</Button>
				</div>
			</div>
			<p>{item.why}</p>

			{/* Every block under the first paragraph is a label and its content,
			    in the same shape: two of these used to be a bold run-in inside a
			    paragraph, which made a card of six blocks read as four and a half. */}
			{item.format ? (
				<>
					<h4>{t("opps.format")}</h4>
					<p>{item.format}</p>
				</>
			) : null}

			{item.must_cover.length > 0 ? (
				<>
					<h4>{t("opps.mustCover")}</h4>
					<ul>
						{item.must_cover.map((line) => (
							<li key={line}>{line}</li>
						))}
					</ul>
				</>
			) : null}

			{item.proof_needed ? (
				<>
					<h4>{t("opps.proof")}</h4>
					<p>{item.proof_needed}</p>
				</>
			) : null}

			{item.questions.length > 0 ? (
				<>
					<h4>{t("opps.questions")}</h4>
					<ul>
						{item.questions.map((question) => (
							<li key={question}>{question}</li>
						))}
					</ul>
				</>
			) : null}

			<Pages label={t("opps.rivalPages")} pages={item.rival_pages} />
			<Pages label={t("opps.yourPages")} pages={item.your_pages} />
		</article>
	);
}

function Pages({ label, pages }: { label: string; pages: CitedPage[] }) {
	if (pages.length === 0) return null;
	return (
		<>
			<h4>{label}</h4>
			<ul>
				{pages.map((page) => (
					<li key={page.url}>
						<a href={page.url} target="_blank" rel="noreferrer nofollow">
							{page.title || page.domain}
						</a>{" "}
						·{" "}
						{/* The host, as the way to who they are: this list names pages,
						    and the report is about the sites behind them. */}
						<Drill className="faint" view={{ kind: "rival", label: page.domain, domain: page.domain }}>
							{page.domain}
						</Drill>
					</li>
				))}
			</ul>
		</>
	);
}

/**
 * Markdown, because every destination takes it — a doc, a ticket, a CMS, a
 * prompt to a writing model — and it survives a paste without negotiating a
 * clipboard format.
 *
 * Nothing here is generated: every line is already on the card. The point is
 * that it leaves in one piece.
 */
function toMarkdown(
	item: Opportunity,
	t: (key: string, vars?: Record<string, string | number>) => string,
	when: string | null,
): string {
	const lines: string[] = [`# ${item.title}`, "", item.why];
	const list = (heading: string, entries: string[]) => {
		if (entries.length === 0) return;
		lines.push("", `## ${heading}`, ...entries.map((entry) => `- ${entry}`));
	};

	if (item.format) lines.push("", `## ${t("opps.format")}`, item.format);
	if (item.proof_needed) lines.push("", `## ${t("opps.proof")}`, item.proof_needed);
	list(t("opps.mustCover"), item.must_cover);
	list(t("opps.questions"), item.questions);
	list(
		t("opps.rivalPages"),
		item.rival_pages.map((page) => `[${page.title || page.domain}](${page.url}) — ${page.domain}`),
	);
	list(
		t("opps.yourPages"),
		item.your_pages.map((page) => `[${page.title || page.domain}](${page.url}) — ${page.domain}`),
	);
	lines.push("", "---", when ? t("opps.source", { when }) : t("opps.sourceUndated"));
	return lines.join("\n");
}

/**
 * Wait for a queued job to end.
 *
 * Polling rather than the event stream: the stream carries the events every
 * screen cares about, and one screen waiting on one job of its own does not
 * belong in it. The interval is cleared on unmount, so navigating away while a
 * report is generating leaves the job running and this component quiet.
 */
function useJobWatch(): (jobId: string) => Promise<string> {
	const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
	useEffect(() => {
		const started = timers.current;
		return () => {
			for (const timer of started) clearTimeout(timer);
		};
	}, []);

	return useCallback(async (jobId: string) => {
		// A report is one long model call; a minute and a half is generous and
		// still ends rather than spinning forever if the worker is down.
		for (let attempt = 0; attempt < 30; attempt++) {
			await new Promise<void>((resolve) => {
				timers.current.push(setTimeout(resolve, 3000));
			});
			const job = (await api.job(jobId)) as { status?: string };
			if (job.status === "done" || job.status === "failed" || job.status === "skipped") {
				return job.status;
			}
		}
		return "running";
	}, []);
}
